import React, { type ReactNode } from 'react';

import type { WidgetContext } from '@wix/chat-widget-common-widgets';
import type { WebAction } from '@wix/common-widget-types';
import {
  Button,
  TextButton,
  type ButtonPriority,
  type ButtonSkin,
} from '@wix/design-system';
import cs from 'classnames';
import s from './WidgetButton.module.scss';
import { iconsMap } from './iconsMap';

interface WidgetButtonProps {
  children: ReactNode;
  type: 'button' | 'textButton';
  respondValues?: string[];
  respondText?: string;
  priority?: ButtonPriority;
  skin?: ButtonSkin;
  webAction?: WebAction;
  context: WidgetContext;
  icon?: {
    place: 'prefix' | 'suffix';
    name: string;
  };
  className?: string;
  onClick?: () => void;
}

export const WidgetButton = ({
  children,
  type,
  priority = 'primary',
  skin = 'standard',
  respondValues = [],
  respondText,
  webAction,
  context,
  icon,
  className = '',
  onClick = () => {},
}: WidgetButtonProps) => {
  const { send, webActions, theme } = context;

  const handleClick = () => {
    if (respondText) {
      send(insertValuesToRespondText(respondText, respondValues));
    }

    if (webAction) {
      if (!webActions) {
        console.error('"webActions" are not defined in the widgets context');
        return;
      }

      for (const [actionName, actionParams] of Object.entries(webAction)) {
        const action = webActions[actionName as keyof typeof webActions];
        if (typeof action === 'function') {
          action(actionParams);
        }
      }
    }

    onClick();
  };

  let prefixIcon, suffixIcon;
  if (icon?.name) {
    const Icon = iconsMap[icon?.name as keyof typeof iconsMap];
    if (Icon) {
      prefixIcon = icon?.place === 'prefix' ? <Icon /> : undefined;
      suffixIcon = icon?.place === 'suffix' ? <Icon /> : undefined;
    }
  }

  const isDarkNonPremiumButton =
    theme === 'dark' && type === 'button' && skin !== 'premium';
  const buttonClasses = cs(className, {
    [s.buttonDark]: isDarkNonPremiumButton && priority === 'primary',
    [s.buttonSecondaryDark]: isDarkNonPremiumButton && priority === 'secondary',
  });
  const textButtonClasses = cs(className, {
    [s.textButtonDark]: theme === 'dark' && type === 'textButton',
  });

  return type === 'button' ? (
    <Button
      size="tiny"
      priority={priority}
      prefixIcon={prefixIcon}
      suffixIcon={suffixIcon}
      onClick={handleClick}
      skin={skin}
      className={buttonClasses}
    >
      {children}
    </Button>
  ) : (
    <TextButton
      size="tiny"
      prefixIcon={prefixIcon}
      suffixIcon={suffixIcon}
      onClick={handleClick}
      className={textButtonClasses}
    >
      {children}
    </TextButton>
  );
};

const insertValuesToRespondText = (text: string, values: string[]) => {
  const valuesString = values.join(', ');

  return text.replace(/<VALUES>/g, valuesString);
};
